import { useEffect, useMemo, useState } from "react";
import { ChevronDown, AlertCircle, CheckCircle } from "lucide-react";
import { getAvailableLambdaFunctions } from "../Oncall/api_utils";
import { generateEvalDataset } from "../../utils/evaluation-functions";
import { environment } from "../../config/environment";

export default function CreateDataset({ userId, onCreated }) {
  const [lambdaName, setLambdaName] = useState("");
  const [datasetName, setDatasetName] = useState("");
  const [patientIdsRaw, setPatientIdsRaw] = useState("");
  const [availableFunctions, setAvailableFunctions] = useState([]);
  const [loading, setLoading] = useState(false);
  const [feedback, setFeedback] = useState({ type: null, message: null });

  useEffect(() => {
    const loadFunctions = async () => {
      try {
        const functionsData = await getAvailableLambdaFunctions();
        let functionKeys;
        if (Array.isArray(functionsData)) {
          functionKeys = functionsData;
        } else if (functionsData && typeof functionsData === "object") {
          functionKeys = Object.keys(functionsData);
        } else {
          functionKeys = [];
        }
        setAvailableFunctions(functionKeys);
      } catch (error) {
        console.error("Failed to load Lambda functions:", error);
        setFeedback({
          type: "error",
          message: `Failed to load available Lambda functions: ${error.message}`,
        });
      }
    };
    loadFunctions();
  }, []);

  const parsedPatientIds = useMemo(
    () =>
      patientIdsRaw
        .split(",")
        .map((s) => s.trim())
        .filter(Boolean),
    [patientIdsRaw],
  );

  const handleSubmit = async (e) => {
    e.preventDefault();
    setFeedback({ type: null, message: null });

    if (!lambdaName) {
      setFeedback({ type: "error", message: "Please select a Lambda function." });
      return;
    }
    if (!datasetName.trim()) {
      setFeedback({ type: "error", message: "Please provide a dataset name." });
      return;
    }
    if (parsedPatientIds.length === 0) {
      setFeedback({
        type: "error",
        message: "Please enter at least one patient ID (comma-separated).",
      });
      return;
    }

    setLoading(true);
    try {
      const result = await generateEvalDataset({
        lambdaName,
        datasetName: datasetName.trim(),
        patientIds: parsedPatientIds,
        createdBy: userId || null,
      });

      setFeedback({
        type: "success",
        message: (
          <>
            Successfully generated eval dataset on Hugging Face:{" "}
            <span className="font-medium">
              Knowtex-ai/{result.dataset_name || `${lambdaName}__${datasetName.trim()}`}
            </span>
            {typeof result.row_count === "number" && (
              <div className="mt-1 text-sm">
                Rows extracted: {result.row_count}
              </div>
            )}
          </>
        ),
      });

      setDatasetName("");
      setPatientIdsRaw("");

      if (onCreated) {
        onCreated();
      }
    } catch (error) {
      console.error("Error generating eval dataset:", error);
      setFeedback({
        type: "error",
        message: error.message || "Failed to generate eval dataset.",
      });
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="max-w-2xl mx-auto">
      <div className="mb-6">
        <h3 className="text-xl font-semibold text-gray-800 mb-2">
          Generate Evaluation Dataset
        </h3>
        <p className="text-gray-600">
          Build a dataset of patient encounters scoped to a specific lambda. The
          ML service extracts the right fields per lambda and uploads the CSV to
          Hugging Face under{" "}
          <code className="text-xs bg-gray-100 px-1 py-0.5 rounded">
            Knowtex-ai/{`{lambda}__{dataset_name}`}
          </code>
          .
        </p>
      </div>

      <form onSubmit={handleSubmit} className="space-y-4">
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">
            Lambda Function
          </label>
          <div className="relative">
            <select
              value={lambdaName}
              onChange={(e) => setLambdaName(e.target.value)}
              disabled={loading}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-ktx-purple focus:border-ktx-purple appearance-none bg-white"
            >
              <option value="">Choose a lambda...</option>
              {availableFunctions.map((fn) => (
                <option key={fn} value={fn}>
                  {fn}
                </option>
              ))}
            </select>
            <ChevronDown className="absolute right-3 top-3 h-4 w-4 text-gray-400 pointer-events-none" />
          </div>
        </div>

        <div>
          <label
            htmlFor="datasetName"
            className="block text-sm font-medium text-gray-700 mb-1"
          >
            Dataset Name
          </label>
          <input
            id="datasetName"
            type="text"
            value={datasetName}
            onChange={(e) => setDatasetName(e.target.value)}
            disabled={loading}
            placeholder="e.g., cardiology-eval-batch-1"
            className="block w-full sm:text-sm border border-gray-300 rounded-md py-2 px-3 focus:ring-ktx-purple focus:border-ktx-purple"
          />
          {lambdaName && datasetName.trim() && (
            <p className="mt-1 text-xs text-gray-500">
              Will be uploaded as{" "}
              <code className="bg-gray-100 px-1 py-0.5 rounded">
                Knowtex-ai/{lambdaName}__{datasetName.trim()}
              </code>
            </p>
          )}
        </div>

        <div>
          <label
            htmlFor="patientIds"
            className="block text-sm font-medium text-gray-700 mb-1"
          >
            Patient IDs (comma-separated)
          </label>
          <textarea
            id="patientIds"
            rows={4}
            value={patientIdsRaw}
            onChange={(e) => setPatientIdsRaw(e.target.value)}
            disabled={loading}
            placeholder="patient-id-1, patient-id-2, patient-id-3"
            className="block w-full sm:text-sm border border-gray-300 rounded-md py-2 px-3 focus:ring-ktx-purple focus:border-ktx-purple font-mono"
          />
          <p className="mt-1 text-xs text-gray-500">
            {parsedPatientIds.length} patient ID
            {parsedPatientIds.length === 1 ? "" : "s"} parsed
          </p>
        </div>

        {feedback.message && (
          <div
            className={`p-3 rounded-md ${
              feedback.type === "error"
                ? "bg-red-50 text-red-700 border border-red-200"
                : "bg-green-50 text-green-700 border border-green-200"
            }`}
          >
            <div className="flex">
              <div className="flex-shrink-0">
                {feedback.type === "error" ? (
                  <AlertCircle className="h-5 w-5 text-red-400" />
                ) : (
                  <CheckCircle className="h-5 w-5 text-green-400" />
                )}
              </div>
              <div className="ml-3">
                {typeof feedback.message === "string" ? (
                  <p className="text-sm">{feedback.message}</p>
                ) : (
                  feedback.message
                )}
              </div>
            </div>
          </div>
        )}

        <div className="flex justify-end">
          <button
            type="submit"
            disabled={loading}
            className={`bg-ktx-purple hover:bg-ktx-purple-dark text-white font-medium py-2 px-4 rounded-md focus:outline-none focus:ring-2 focus:ring-ktx-purple focus:ring-offset-2 transition-colors ${
              loading ? "opacity-70 cursor-not-allowed" : ""
            }`}
          >
            {loading ? (
              <div className="flex items-center">
                <svg
                  className="animate-spin -ml-1 mr-2 h-4 w-4 text-white"
                  xmlns="http://www.w3.org/2000/svg"
                  fill="none"
                  viewBox="0 0 24 24"
                >
                  <circle
                    className="opacity-25"
                    cx="12"
                    cy="12"
                    r="10"
                    stroke="currentColor"
                    strokeWidth="4"
                  ></circle>
                  <path
                    className="opacity-75"
                    fill="currentColor"
                    d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                  ></path>
                </svg>
                Generating...
              </div>
            ) : (
              "Generate Dataset"
            )}
          </button>
        </div>
      </form>

      <div className="mt-8 p-4 bg-blue-50 rounded-md border border-blue-100">
        <h4 className="text-sm font-medium text-blue-800 mb-2">
          What gets pulled
        </h4>
        <ul className="space-y-1 text-sm text-blue-700 list-disc pl-5">
          <li>
            Every attribute on each <code>{environment.summarizeJobsTable}</code> row for the
            patient (queried via the <code>patient_id-index</code> GSI). One
            row per encounter; columns are the union of all attributes seen.
          </li>
          <li>
            Quality filters: transcripts shorter than 600 chars or with
            excessive consecutive repetition are skipped.
          </li>
          <li>
            The lambda choice tags the dataset (HF repo prefix) but does not
            change which fields are extracted - downstream eval picks the
            relevant columns for each lambda.
          </li>
        </ul>
      </div>
    </div>
  );
}
